'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '@/lib/api'
import { getTenantCacheKey } from '@/lib/tenantCache'

interface Service {
  id: string
  name: string
  description: string
  price: number
  duration_minutes: number
  status?: string
}

interface ServicesContextType {
  services: Service[]
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

const ServicesContext = createContext<ServicesContextType | undefined>(undefined)

const SERVICES_CACHE_KEY = 'services_cache'
const CACHE_DURATION = 10 * 60 * 1000 // 10 minutos

export function ServicesProvider({ children, skipFetch = false }: { children: ReactNode; skipFetch?: boolean }) {
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const loadServices = async () => {
    try {
      setLoading(true)
      setError(null)

      // Intentar cargar desde caché (SSR safe)
      if (typeof window !== 'undefined') {
        const cached = localStorage.getItem(getTenantCacheKey(SERVICES_CACHE_KEY))
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached)
            const now = Date.now()
            
            if (now - timestamp < CACHE_DURATION) {
              setServices(data)
              setLoading(false)
              // Actualizar en background solo si no estamos en admin
              if (!skipFetch) {
                api.get('/public/services').then(freshData => {
                  setServices(Array.isArray(freshData) ? freshData : [])
                  localStorage.setItem(getTenantCacheKey(SERVICES_CACHE_KEY), JSON.stringify({
                    data: freshData,
                    timestamp: Date.now()
                  }))
                }).catch(() => {})
              }
              return
            }
          } catch (e) {
            localStorage.removeItem(getTenantCacheKey(SERVICES_CACHE_KEY))
          }
        }
      }

      // If skipFetch and we have no cache, don't fetch
      if (skipFetch) {
        setLoading(false)
        return
      }

      // Cargar desde API
      const data = await api.get('/public/services')
      const servicesData = Array.isArray(data) ? data : []
      setServices(servicesData)
      
      // Guardar en caché (solo en cliente)
      if (typeof window !== 'undefined') {
        localStorage.setItem(getTenantCacheKey(SERVICES_CACHE_KEY), JSON.stringify({
          data: servicesData,
          timestamp: Date.now()
        }))
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Error loading services'))
      console.error('Error loading services:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadServices()
  }, [])

  const refetch = async () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(getTenantCacheKey(SERVICES_CACHE_KEY))
    }
    await loadServices()
  }

  return (
    <ServicesContext.Provider value={{ services, loading, error, refetch }}>
      {children}
    </ServicesContext.Provider>
  )
}

export function useServices() {
  const context = useContext(ServicesContext)
  if (context === undefined) {
    throw new Error('useServices must be used within a ServicesProvider')
  }
  return context
}
